import { Box, InputAdornment, MenuItem, TextField } from '@mui/material';

import { PolicyInformationInfoTable } from './policy-information-info-table';
import {
  tableFieldSx,
  OTHER_LIABILITY_VALUE,
  type PolicyDraftSetter,
  getLiabilitySelectValue,
  accidentLiabilityOptions,
  getLiabilityValueAfterSelect,
  diseasePolicyLiabilityOptions,
  diseaseEmployeeLiabilityOptions,
} from './policy-information-edit.helpers';

import type { PolicyEditDraft, PolicyFieldErrors } from './policy-information.types';

type PolicyInformationLiabilityLimitsProps = {
  draft: PolicyEditDraft;
  setDraft: PolicyDraftSetter;
  fieldErrors: PolicyFieldErrors;
};

export function PolicyInformationLiabilityLimits({
  draft,
  setDraft,
  fieldErrors,
}: Readonly<PolicyInformationLiabilityLimitsProps>) {
  const limits = [
    {
      key: 'liability-accident',
      label: 'Bodily injury by accident - Each accident',
      field: 'bodilyInjuryAccidentLimit' as const,
      options: accidentLiabilityOptions,
      selectTestId: 'liability-accident-select',
      customTestId: 'liability-accident-custom-input',
    },
    {
      key: 'liability-policy',
      label: 'Bodily injury by disease - Policy limit',
      field: 'bodilyInjuryDiseasePolicyLimit' as const,
      options: diseasePolicyLiabilityOptions,
      selectTestId: 'liability-disease-policy-select',
      customTestId: 'liability-disease-policy-custom-input',
    },
    {
      key: 'liability-employee',
      label: 'Bodily injury by disease - Each employee',
      field: 'bodilyInjuryDiseaseEmployeeLimit' as const,
      options: diseaseEmployeeLiabilityOptions,
      selectTestId: 'liability-disease-employee-select',
      customTestId: 'liability-disease-employee-custom-input',
    },
  ];

  return (
    <PolicyInformationInfoTable
      dataTestId="liability-limits-table"
      title="Employer's liability limits"
      columns={['Liability type', 'Limit']}
      rows={limits.map((limit) => {
        const value = draft[limit.field];
        const selectValue = getLiabilitySelectValue(value, limit.options);
        const error = fieldErrors[limit.field];
        return {
          key: limit.key,
          cells: [
            limit.label,
            <Box key={limit.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                select
                fullWidth
                size="small"
                sx={tableFieldSx}
                value={selectValue}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    [limit.field]: getLiabilityValueAfterSelect(
                      current[limit.field],
                      event.target.value,
                      limit.options
                    ),
                  }))
                }
                data-testid={limit.selectTestId}
              >
                {limit.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem value={OTHER_LIABILITY_VALUE}>Other</MenuItem>
              </TextField>
              {selectValue === OTHER_LIABILITY_VALUE && (
                <TextField
                  fullWidth
                  size="small"
                  sx={tableFieldSx}
                  value={value}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: { inputMode: 'numeric', maxLength: 10 } }}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [limit.field]: event.target.value.replace(/\D/g, ''),
                    }))
                  }
                  error={Boolean(error)}
                  helperText={error}
                  data-testid={limit.customTestId}
                />
              )}
            </Box>,
          ],
        };
      })}
    />
  );
}
